import { memo } from "react";

import {
  ShopifyProvider,
  ProductProvider as HydrogenProductProvider,
} from "@shopify/hydrogen-react";
import { ErrorBoundary } from "react-error-boundary";
import useSWR, { SWRConfig } from "swr";

import { LoadingOverlay } from "@components/LoadingOverlay";
import getProductByIdQuery from "@graphql/get-product-by-id.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { ProductHeroPlaceholder } from "@react/sections/ProductHero/ProductHeroPlaceholder.view";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type OPProductConnection,
  type ProductHeroSettings,
} from "@root/tablescape/state/product";
import { getShopifyGlobalId } from "@root/tablescape/utilities";
import {
  STOREFRONT_ACCESS_TOKEN,
  STOREFRONT_API_VERSION,
  SWR_SETTINGS,
  STOREFRONT_API_URL,
} from "@tablescape/constants";

const ProductProviderBase = ({ children }: { children: React.ReactNode }) => {
  const {
    initialVariantId,
    meta: {
      localization: { country, language },
    },
    productGalleryCarousel: { lcpMedia },
    productId,
    settings: {
      crossProductBridgeOptionsCount,
      productOptionCategoriesCount,
      productSelectorExperience,
      showCrossProductBridge,
      theme,
      version,
    },
  } = useSectionSettings<ProductHeroSettings>();

  const { data } = useSWR<{ data?: { product: OPProductConnection } }>(
    productId && country
      ? [
          STOREFRONT_API_URL,
          getProductByIdQuery,
          {
            productId: getShopifyGlobalId("Product", productId),
            country,
            language,
          },
        ]
      : null,
    storefrontAPIFetcher,
    {}
  );

  if (!data?.data?.product) {
    return version === "v2" ? (
      <ProductHeroPlaceholder
        animateIn={false}
        crossProductBridgeOptionsCount={crossProductBridgeOptionsCount}
        lcpMedia={lcpMedia}
        productOptionCategoriesCount={productOptionCategoriesCount}
        productSelectorExperience={productSelectorExperience}
        region="section"
        showCrossProductBridge={showCrossProductBridge}
        theme={theme}
      />
    ) : null;
  }

  return (
    <ErrorBoundary fallback={<LoadingOverlay show />}>
      <ShopifyProvider
        countryIsoCode={window.Shopify.country}
        languageIsoCode={window.Shopify.locale}
        storeDomain={window.location.origin}
        storefrontApiVersion={STOREFRONT_API_VERSION}
        storefrontToken={STOREFRONT_ACCESS_TOKEN}
      >
        <HydrogenProductProvider
          data={data.data.product}
          initialVariantId={getShopifyGlobalId(
            "ProductVariant",
            initialVariantId
          )}
        >
          <SWRConfig value={SWR_SETTINGS}>{children}</SWRConfig>
        </HydrogenProductProvider>
      </ShopifyProvider>
    </ErrorBoundary>
  );
};

export const ProductProvider = memo(ProductProviderBase);
